import { createContext, useContext, useMemo } from 'react';
import {
	toAllowedSet,
	type Permission,
	type PermissionValue,
	can as _can,
} from '@/lib/permissions';

type Ctx = {
	allowed: Set<PermissionValue>;
	can: typeof _can;
	has: (p: PermissionValue) => boolean;
};
const PermissionsCtx = createContext<Ctx | null>(null);

export function PermissionsProvider({
	permissions,
	children,
}: {
	permissions: Permission[];
	children: React.ReactNode;
}) {
	const allowed = useMemo(() => toAllowedSet(permissions), [permissions]);
	const value = useMemo<Ctx>(
		() => ({ allowed, can: (a, b) => _can(a, b), has: (p) => allowed.has(p) }),
		[allowed]
	);
	return (
		<PermissionsCtx.Provider value={value}>{children}</PermissionsCtx.Provider>
	);
}

export function usePermissions() {
	const ctx = useContext(PermissionsCtx);
	if (!ctx)
		throw new Error('usePermissions must be inside <PermissionsProvider/>');
	return ctx;
}
